import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PolicyDocumentsTab from './PolicyDocumentsTab';
import PolicyNotesTab from './PolicyNotesTab';
import PolicyWhatsAppTab from './PolicyWhatsAppTab';
import PolicyEmailTab from './PolicyEmailTab';

interface Policy {
  id: number;
  policy_number: string;
  product_name: string;
  insurer: string;
  status: 'active' | 'cancelled';
  start_date: string;
  end_date?: string;
  renewal_date?: string;
  premium_amount: number;
  premium_collection_date?: string;
  whatsapp_notification?: boolean;
  email_notification?: boolean;
  cancellation_date?: string;
  cancellation_reason?: string;
  client_id: number;
  quote_id?: number;
}

interface ClientPolicyDetailProps {
  policyId: string;
  onBack: () => void;
}

const ClientPolicyDetail = ({ policyId, onBack }: ClientPolicyDetailProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('policyDetails');
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Local editable copies — for text/textarea we debounce on blur; for selects/dates we save on change
  const [editablePolicyNumber, setEditablePolicyNumber] = useState('');
  const [editableInsurer, setEditableInsurer] = useState('');
  const [editablePremiumAmount, setEditablePremiumAmount] = useState('');
  const [editableCancellationReason, setEditableCancellationReason] = useState('');
  const [clientProducts, setClientProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    fetchData();
  }, [policyId]);

  useEffect(() => {
  if (!policy?.quote_id) {
    setClientProducts([]);
    return;
  }
  fetchPolicyProducts();
}, [policy?.quote_id, policyId]);

const fetchPolicyProducts = async () => {
  setLoadingProducts(true);
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/policy-products/${policyId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const products = res.data?.data || [];
    setClientProducts(products);
  } catch (err) {
    console.error('Failed to fetch policy products:', err);
    setClientProducts([]);
  } finally {
    setLoadingProducts(false);
  }
};

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const policyRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/policies/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const policyData = policyRes.data.data;
      setPolicy(policyData);

      // Seed editable copies
      setEditablePolicyNumber(policyData.policy_number || '');
      setEditableInsurer(policyData.insurer || '');
      setEditablePremiumAmount(policyData.premium_amount?.toString() || '');
      setEditableCancellationReason(policyData.cancellation_reason || '');

    } catch (err) {
      console.error('Failed to fetch policy:', err);
      setError('Failed to load policy details');
    } finally {
      setLoading(false);
    }
  };

  // Sends full policy with the changed field merged in — backend requires full body
  const saveField = async (changes: Partial<Policy>, activityDescription: string) => {
    if (!policy) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Merge changes onto the current policy to build the full payload
      const merged = { ...policy, ...changes };

      const payload = {
        clientId: merged.client_id,
        policy_number: merged.policy_number,
        product_name: merged.product_name || 'Policy',
        insurer: merged.insurer || null,
        status: merged.status,
        start_date: merged.start_date,
        end_date: merged.end_date || null,
        renewal_date: merged.renewal_date || null,
        premium_amount: merged.premium_amount,
        premium_collection_date: merged.premium_collection_date || null,
        whatsapp_notification: merged.whatsapp_notification || false,
        email_notification: merged.email_notification || false,
        cancellation_date: merged.cancellation_date || null,
        cancellation_reason: merged.cancellation_reason || null,
        quote_id: merged.quote_id || null,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/policies/${policyId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        const updated = response.data.data;
        setPolicy(updated);

        // Re-seed editable copies
        setEditablePolicyNumber(updated.policy_number || '');
        setEditableInsurer(updated.insurer || '');
        setEditablePremiumAmount(updated.premium_amount?.toString() || '');
        setEditableCancellationReason(updated.cancellation_reason || '');

                // Log activity
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/policy-notes`,
            {
              policy_id: parseInt(policyId!),
              action_type: 'EDITED',
              description: activityDescription
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (logErr) {
          console.warn('Failed to log policy activity (non-blocking):', logErr);
        }

        window.dispatchEvent(new Event('refresh-policy-notes'));

        setSuccess('Policy updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Failed to update policy:', err);
      setError(err.response?.data?.message || 'Failed to update policy');
      setTimeout(() => setError(null), 4000);

      // Revert editable copies to server state
      if (policy) {
        setEditablePolicyNumber(policy.policy_number || '');
        setEditableInsurer(policy.insurer || '');
        setEditablePremiumAmount(policy.premium_amount?.toString() || '');
        setEditableCancellationReason(policy.cancellation_reason || '');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Field handlers ----

  const handlePolicyNumberBlur = () => {
    if (!policy) return;
    if (editablePolicyNumber.trim() === policy.policy_number) return;
    if (!editablePolicyNumber.trim()) {
      setEditablePolicyNumber(policy.policy_number);
      return;
    }
    saveField(
      { policy_number: editablePolicyNumber.trim() },
      `Policy number changed to: ${editablePolicyNumber.trim()}`
    );
  };

  const handleInsurerBlur = () => {
    if (!policy) return;
    const newVal = editableInsurer.trim();
    if (newVal === (policy.insurer || '')) return;
    saveField(
      { insurer: newVal },
      `Insurer changed to: ${newVal || 'None'}`
    );
  };

  const handlePremiumAmountBlur = () => {
    if (!policy) return;
    const parsed = parseFloat(editablePremiumAmount);
    if (isNaN(parsed) || parsed <= 0) {
      setEditablePremiumAmount(policy.premium_amount?.toString() || '');
      return;
    }
    if (parsed === policy.premium_amount) return;
    saveField(
      { premium_amount: parsed },
      `Premium amount changed to: R ${parsed.toFixed(2)}`
    );
  };

  const handleCancellationReasonBlur = () => {
    if (!policy) return;
    const newVal = editableCancellationReason;
    if (newVal === (policy.cancellation_reason || '')) return;
    saveField(
      { cancellation_reason: newVal },
      `Cancellation reason updated`
    );
  };

  const handlePremiumCollectionDayChange = (value: string) => {
    if (!policy) return;

    let newDate: string | null = null;

    if (value === 'last-day') {
      const today = new Date();
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const y = lastDay.getFullYear();
      const m = String(lastDay.getMonth() + 1).padStart(2, '0');
      const d = String(lastDay.getDate()).padStart(2, '0');
      newDate = `${y}-${m}-${d}`;
    } else if (value) {
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(parseInt(value)).padStart(2, '0');
      newDate = `${y}-${m}-${d}`;
    }

    saveField(
      { premium_collection_date: newDate || undefined },
      `Premium collection date set to: ${value || 'Not set'}`
    );
  };

  const handleStartDateChange = (value: string) => {
    if (!policy || !value) return;
    if (value === policy.start_date?.split('T')[0]) return;
    saveField(
      { start_date: value },
      `Inception date changed to: ${new Date(value).toLocaleDateString()}`
    );
  };

  const handleEndDateChange = (value: string) => {
    if (!policy) return;
    const currentVal = policy.end_date?.split('T')[0] || '';
    if (value === currentVal) return;
    saveField(
      { end_date: value || undefined },
      `Termination date changed to: ${value ? new Date(value).toLocaleDateString() : 'Not set'}`
    );
  };

  const handleRenewalDateChange = (value: string) => {
    if (!policy) return;
    const currentVal = policy.renewal_date?.split('T')[0] || '';
    if (value === currentVal) return;
    saveField(
      { renewal_date: value || undefined },
      `Renewal date changed to: ${value ? new Date(value).toLocaleDateString() : 'Not set'}`
    );
  };

  const handleStatusChange = (newStatus: string) => {
    if (!policy || policy.status === newStatus) return;
    saveField(
      { status: newStatus as 'active' | 'cancelled' },
      `Status changed to: ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`
    );
  };

  const handleCancellationDateChange = (value: string) => {
    if (!policy) return;
    const currentVal = policy.cancellation_date?.split('T')[0] || '';
    if (value === currentVal) return;
    saveField(
      { cancellation_date: value || undefined },
      `Cancellation date changed to: ${value ? new Date(value).toLocaleDateString() : 'Not set'}`
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
  };

  // Determine current premium collection day value for the select
  const getPremiumCollectionDayValue = (): string => {
    if (!policy?.premium_collection_date) return '';
    const date = new Date(policy.premium_collection_date);
    const day = date.getDate();
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    if (day === lastDayOfMonth) return 'last-day';
    return String(day);
  };

    // Shortcut: jump to the WhatsApp / Email tab. Falls back gracefully if policy isn't loaded.
  const goToTab = (tab: 'whatsapp' | 'email') => {
    setActiveTab(tab);
  };

   // ---- Product display helpers (read-only) ----

  const getProductTypeDisplay = (type: string): string => {
    const map: Record<string, string> = {
      'properties': 'Property',
      'property': 'Property',
      'vehicles': 'Vehicle',
      'vehicle': 'Vehicle',
      'household-contents': 'Household Contents',
      'household': 'Household Contents',
      'risk-items': 'Risk Item',
      'risk-item': 'Risk Item',
      'risk_item': 'Risk Item',
      'water-craft': 'Water Craft',
      'watercraft': 'Water Craft',
      'trailer-caravans': 'Trailer/Caravan',
      'trailer': 'Trailer/Caravan',
      'value-added': 'Value Added',
      'value_added': 'Value Added',
    };
    return map[type] || type;
  };

  const getProductName = (product: any, type: string): string => {
    const t = type === 'risk-item' || type === 'risk_item' ? 'risk-items' : type;
    switch (t) {
      case 'properties':
        return product.property_type || product.address_line_1 || 'Property';
      case 'vehicles':
        return `${product.make || ''} ${product.model || ''}`.trim() || 'Vehicle';
      case 'household-contents':
        return product.property_type || 'Household Contents';
      case 'risk-items':
        return product.item_name || product.itemName || product.description || 'Risk Item';
      case 'water-craft':
        return product.vessel_name || product.vesselName || 'Water Craft';
      case 'trailer-caravans':
        return product.vehicle_type || product.type || 'Trailer/Caravan';
      case 'value-added':
        return product.product_type || product.name || 'Value Added';
      default:
        return product.name || 'Product';
    }
  };

  const getProductValue = (product: any): number => {
    const raw =
      product.insured_value ??
      product.insuredValue ??
      product.value ??
      product.total_sum_insured ??
      product.totalSumInsured ??
      0;
    const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
    return isNaN(n) ? 0 : n;
  };

  const getProductDescription = (product: any, type: string): string => {
    const t = type === 'risk-item' || type === 'risk_item' ? 'risk-items' : type;
    switch (t) {
      case 'vehicles':
        return `${product.make || ''} ${product.model || ''} ${product.year || ''}`.trim() || 'Vehicle';
      case 'risk-items':
        return product.item_name || product.itemName || product.description || 'Risk Item';
      case 'water-craft':
        return product.vessel_name || product.vesselName || 'Water Craft';
      case 'trailer-caravans':
        return `${product.make || ''} ${product.model || ''}`.trim() ||
          product.vehicle_type || 'Trailer/Caravan';
      case 'value-added':
        return product.description || product.product_type || product.name || 'Value Added';
      default:
        return product.description || product.name || 'Product';
    }
  };

  const getPropertyAddress = (product: any): string => {
    const parts = [
      product.address_line_1 || product.addressLine1,
      product.suburb,
      product.city || product.city_town,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading policy...</div>;
  }

  if (!policy) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error || 'Policy not found'}</p>
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800">
          ← Back to Policies
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="float-right text-red-700 hover:text-red-900">×</button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Policy Details</h3>
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back to List
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(['policyDetails', 'documents', 'notes', 'whatsapp', 'email'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'policyDetails' ? 'Policy Details' :
                tab === 'whatsapp' ? 'WhatsApp' :
                tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'policyDetails' && (
          <div className="bg-white shadow-md rounded-lg p-6 border">
            <div className="grid grid-cols-2 gap-6">
              {/* Policy Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
                <input
                  type="text"
                  value={editablePolicyNumber}
                  onChange={(e) => setEditablePolicyNumber(e.target.value)}
                  onBlur={handlePolicyNumberBlur}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

              {/* Insurer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurer</label>
                <input
                  type="text"
                  value={editableInsurer}
                  onChange={(e) => setEditableInsurer(e.target.value)}
                  onBlur={handleInsurerBlur}
                  disabled={isSaving}
                  placeholder="Enter insurer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

                            {/* Premium Collection Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Premium Collection Date</label>
                <div className="flex items-center gap-2">
                  <select
                    value={getPremiumCollectionDayValue()}
                    onChange={(e) => handlePremiumCollectionDayChange(e.target.value)}
                    disabled={isSaving}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Not set</option>
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                    <option value="last-day">Last Day of Month</option>
                  </select>

                  {/* WhatsApp shortcut */}
                  <button
                    type="button"
                    onClick={() => goToTab('whatsapp')}
                    title="Send WhatsApp"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-md transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </button>

                  {/* Email shortcut */}
                  <button
                    type="button"
                    onClick={() => goToTab('email')}
                    title="Send Email"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-md transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email
                  </button>
                </div>
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

              {/* Inception Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inception Date</label>
                <input
                  type="date"
                  value={policy.start_date?.split('T')[0] || ''}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

              {/* Renewal Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Renewal Date</label>
                <input
                  type="date"
                  value={policy.renewal_date?.split('T')[0] || ''}
                  onChange={(e) => handleRenewalDateChange(e.target.value)}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

              {/* Premium Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Premium Amount</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    type="number"
                    value={editablePremiumAmount}
                    onChange={(e) => setEditablePremiumAmount(e.target.value)}
                    onBlur={handlePremiumAmountBlur}
                    disabled={isSaving}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

              {/* Termination Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Termination Date</label>
                <input
                  type="date"
                  value={policy.end_date?.split('T')[0] || ''}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={policy.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
              </div>

              {/* Cancellation fields — only when cancelled */}
              {policy.status === 'cancelled' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Date</label>
                    <input
                      type="date"
                      value={policy.cancellation_date?.split('T')[0] || ''}
                      onChange={(e) => handleCancellationDateChange(e.target.value)}
                      disabled={isSaving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Reason</label>
                    <textarea
                      value={editableCancellationReason}
                      onChange={(e) => setEditableCancellationReason(e.target.value)}
                      onBlur={handleCancellationReasonBlur}
                      disabled={isSaving}
                      rows={3}
                      placeholder="Enter cancellation reason..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                  </div>
                </>
              )}
            </div>

                        {/* Insured Products Section (read-only) */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <h3 className="text-lg font-semibold text-gray-800">Insured Products</h3>
                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                  {clientProducts.length}
                </span>
              </div>

              {loadingProducts ? (
                <p className="text-gray-500 text-sm">Loading products...</p>
              ) : !policy?.quote_id ? (
                <p className="text-gray-500 text-sm italic">
                  No quote linked to this policy — products cannot be displayed.
                </p>
              ) : clientProducts.length === 0 ? (
                <p className="text-gray-500 text-sm italic">
                  No insured products found for this policy.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {clientProducts.map((product: any) => (
                    <div
                      key={`${product.product_type}-${product.id}`}
                      className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-gray-800 text-sm truncate">
                          {getProductName(product, product.product_type)}
                        </h4>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full whitespace-nowrap ml-2 flex-shrink-0">
                          {getProductTypeDisplay(product.product_type)}
                        </span>
                      </div>

                      {(product.product_type === 'properties' ||
                        product.product_type === 'household-contents') ? (
                        <p className="text-xs text-gray-600 mb-2">
                          {getPropertyAddress(product)}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-600 mb-2 truncate">
                          {getProductDescription(product, product.product_type)}
                        </p>
                      )}

                      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Insured Value</span>
                        <span className="font-medium text-green-600 text-sm">
                          {formatCurrency(getProductValue(product))}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {/* Policy Questions Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <PolicyQuestions
                policyId={policyId}
                clientId={policy?.client_id?.toString() || ''}
                clientName={policy?.policy_number || 'Policy'}
              />
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <PolicyNotesTab policyId={parseInt(policyId!)} />
  </div>
)}

        {activeTab === 'documents' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <PolicyDocumentsTab
      policyId={parseInt(policyId!)}
      onDocumentChange={() =>
        window.dispatchEvent(new Event('refresh-policy-notes'))
      }
    />
  </div>
)}

                {activeTab === 'whatsapp' && (
          <div className="bg-white shadow-md rounded-lg p-6 border">
            <PolicyWhatsAppTab
              policyId={parseInt(policyId!)}
              clientId={policy.client_id.toString()}
              policyNumber={policy.policy_number}
            />
          </div>
        )}

                {activeTab === 'email' && (
          <div className="bg-white shadow-md rounded-lg p-6 border">
            <PolicyEmailTab
              policyId={parseInt(policyId!)}
              clientId={policy.client_id.toString()}
              policyNumber={policy.policy_number}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PolicyQuestions Component
// ============================================================================
interface PolicyQuestionsProps {
  policyId: string;
  clientId: string;
  clientName: string;
}

interface PolicyQuestion {
  id: number;
  question: string;
  order: number;
  is_active: boolean;
}

interface PolicyAnswer {
  question_id: number;
  answer: boolean;
  answered_by?: string;
  created_at?: string;
}

const PolicyQuestions: React.FC<PolicyQuestionsProps> = ({ policyId, clientId, clientName }) => {
  const [questions, setQuestions] = useState<PolicyQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  const [answerTimestamps, setAnswerTimestamps] = useState<Record<number, string>>({});
  const [answerUserNames, setAnswerUserNames] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loggedInUserName, setLoggedInUserName] = useState<string>('');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const name = user.username || user.email || 'User';
        const surname = user.surname || '';
        const fullName = `${name} ${surname}`.trim();
        setLoggedInUserName(fullName || name || 'User');
      } catch (e) {
        setLoggedInUserName('User');
      }
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [policyId]);

  const fetchQuestions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '';

      const questionsResponse = await fetch(`${API_URL}/api/policy-questions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!questionsResponse.ok) {
        throw new Error('Failed to fetch questions');
      }

      const questionsData = await questionsResponse.json();

      let questionsList: PolicyQuestion[] = [];
      if (questionsData && questionsData.success && Array.isArray(questionsData.data)) {
        questionsList = questionsData.data;
      } else if (Array.isArray(questionsData)) {
        questionsList = questionsData;
      }

      const answersResponse = await fetch(`${API_URL}/api/policy-questions/answers/${policyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      let existingAnswers: Record<number, boolean> = {};
      let existingTimestamps: Record<number, string> = {};
      let existingUserNames: Record<number, string> = {};

      if (answersResponse.ok) {
        const answersData = await answersResponse.json();

        if (answersData && answersData.success && Array.isArray(answersData.data)) {
          answersData.data.forEach((answer: PolicyAnswer) => {
            existingAnswers[answer.question_id] = answer.answer;
            if (answer.created_at) {
              existingTimestamps[answer.question_id] = answer.created_at;
            }
            if (answer.answered_by) {
              existingUserNames[answer.question_id] = answer.answered_by;
            }
          });
        }
      }

      setQuestions(questionsList);
      setAnswers(existingAnswers);
      setAnswerTimestamps(existingTimestamps);
      setAnswerUserNames(existingUserNames);
    } catch (error) {
      console.error('Error fetching policy questions:', error);
      setError('Failed to load questions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, value: boolean) => {
    if (answerTimestamps[questionId] === undefined) {
      setAnswers(prev => ({ ...prev, [questionId]: value }));
    }
  };

  const handleSaveAnswers = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '';

      let signedOffBy = '';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'User';
          const surname = user.surname || '';
          signedOffBy = `${name} ${surname}`.trim() || name || 'User';
        } catch (e) {
          signedOffBy = 'User';
        }
      }

      const answersToSave = Object.entries(answers).map(([questionId, answer]) => ({
        policy_id: parseInt(policyId),
        question_id: parseInt(questionId),
        answer: answer,
        answered_by: signedOffBy
      }));

      const response = await fetch(`${API_URL}/api/policy-questions/answers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers: answersToSave }),
      });

      if (!response.ok) {
        throw new Error('Failed to save answers');
      }

      await fetchQuestions();
      setSuccess('Answers saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error saving answers:', error);
      setError(error.message || 'Failed to save answers');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border">
        <div className="text-center text-gray-500">Loading questions...</div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border">
        <h4 className="text-md font-semibold text-gray-900 mb-2">Policy Questions</h4>
        <p className="text-sm text-gray-500">No questions configured for policies.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border">
      <h4 className="text-md font-semibold text-gray-900 mb-4">Policy Questions</h4>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">
          {success}
        </div>
      )}

      <div className="space-y-4">
        {questions.map((question) => {
          const isAnswered = answerTimestamps[question.id] !== undefined;
          const answeredBy = answerUserNames[question.id] || '';

          return (
            <div
              key={question.id}
              className={`p-4 rounded-lg border transition-colors ${
                isAnswered
                  ? 'bg-gray-100 border-gray-300'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <p className={`text-sm mb-3 ${isAnswered ? 'text-gray-500' : 'text-gray-900'}`}>
                {question.question}
              </p>

              <div className="flex space-x-6">
                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === true}
                    onChange={() => handleAnswerChange(question.id, true)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${
                      isAnswered
                        ? 'text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'text-blue-600 border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>
                    Yes
                  </span>
                </label>

                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === false}
                    onChange={() => handleAnswerChange(question.id, false)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${
                      isAnswered
                        ? 'text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'text-blue-600 border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>
                    No
                  </span>
                </label>
              </div>

              {isAnswered && answerTimestamps[question.id] && (
                <div className="mt-3 flex items-center">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600 border border-gray-300">
                    <svg className="w-3 h-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="mr-1 font-semibold">{answeredBy || loggedInUserName || 'User'}</span>
                    <span>•</span>
                    <span className="ml-1">{formatDateTime(answerTimestamps[question.id])}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(answers).length > Object.keys(answerTimestamps).length && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveAnswers}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Answers'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientPolicyDetail;